import { safeJson } from 'shared/utils/json';

class SafeStorage {
  constructor(private _storage: Storage) {}

  public async add<Value>(key: string, value: Value) {
    this._storage.setItem(key, getSafeValue(value));

    return value;
  }

  /**
   * @summary Функция для безопасного получения данных из storage
   * @param key Уникальный ключ в storage
   * @param options Указать parse = true если нужно распарсить из json
   * @returns Значение тип Result generic
   */
  public get<Result, T extends 'sync' | 'async' = 'async'>(
    key: string,
    options: { parse?: boolean; type: T } = { parse: false, type: 'async' as T },
  ): T extends 'async' ? Promise<Awaited<Result | undefined>> : Result | undefined {
    const valueFromLs = this._storage.getItem(key);

    if (options?.parse && valueFromLs) {
      const { hasError, value: parsedValueFromLs } = safeJson.from<Result>(valueFromLs);

      if (!hasError)
        return (
          options.type === 'async'
            ? Promise.resolve(parsedValueFromLs)
            : parsedValueFromLs
        ) as never;
    }

    return (
      options.type === 'async' ? Promise.resolve(valueFromLs) : valueFromLs
    ) as never;
  }

  public async delete(key: string, options = { returning: false }) {
    let valueToDelete = null;

    if (options.returning) {
      valueToDelete = await this.get<string, 'async'>(key);
    }

    this._storage.removeItem(key);

    return valueToDelete;
  }
}

export const safeLs = new SafeStorage(window.localStorage);
export const safeSs = new SafeStorage(window.sessionStorage);

function getSafeValue<Value>(value: Value) {
  return typeof value === 'string' ? value : safeJson.to(value);
}
